import { useEffect, useMemo, useState } from 'react';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import useRestaurantRawMaterialCategories from '../../../hooks/restaurant/useRestaurantRawMaterialCategories';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import { RestaurantRawMaterialCategoryFilters as CategoryFilters } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantPageRequest } from '../../../types/restaurant/RestaurantRecipeTypes';

import {
  RawMaterialCategoryEmpty,
  RawMaterialCategoryFilters,
  RawMaterialCategoryGrid,
  RawMaterialCategoryHeader,
  RawMaterialCategorySkeleton,
  RawMaterialCategoryTable,
} from './components';
import { RawMaterialCategoryActiveFilter } from './components/RawMaterialCategoryFilters';
import RawMaterialCategoryFormModal from './RawMaterialCategoryFormModal';

const RestaurantRawMaterialCategoriesScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');

  const [activeFilter, setActiveFilter] =
    useState<RawMaterialCategoryActiveFilter>('ACTIVE');

  // =====================================================
  // 🔹 Paginación
  // =====================================================

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort] = useState([
    'displayOrder,asc',
    'name,asc',
    'id,asc',
  ]);

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const debouncedSearch = useDebouncedValue(search, 350);

  const categories =
    useRestaurantRawMaterialCategories(branchId);

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((data) => {
        setBranches(data);

        if (data.length > 0) {
          setBranchId(data[0].id);
        }
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  // =====================================================
  // 🔹 Construcción de petición
  // =====================================================

  const request = useMemo<
    RestaurantPageRequest<CategoryFilters>
  >(
    () => ({
      page,
      size,
      sort,
      filters: {
        search: debouncedSearch.trim() || null,

        active:
          activeFilter === 'ALL'
            ? null
            : activeFilter === 'ACTIVE',
      },
    }),
    [
      activeFilter,
      debouncedSearch,
      page,
      size,
      sort,
    ]
  );

  // =====================================================
  // 🔹 Cargar categorías
  // =====================================================

  useEffect(() => {
    if (branchId) {
      categories.load(request);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, request]);

  // =====================================================
  // 🔹 Navegación
  // =====================================================

  const previousPage = () => {
    setPage((current) => Math.max(0, current - 1));
  };

  // =====================================================
  // 🔹 Eventos del header
  // =====================================================

  const handleBranchChange = (nextBranchId: number) => {
    setSearch('');
    setActiveFilter('ACTIVE');
    setPage(0);
    setBranchId(nextBranchId);
  };

  // =====================================================
  // 🔹 Eventos de filtros
  // =====================================================

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleActiveChange = (
    value: RawMaterialCategoryActiveFilter
  ) => {
    setActiveFilter(value);
    setPage(0);
  };

  // =====================================================
  // 🔹 Eventos de paginación
  // =====================================================

  const handlePageSizeChange = (value: number) => {
    setSize(value);
    setPage(0);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        {/* =====================================================
            🔹 Header
        ===================================================== */}

        <RawMaterialCategoryHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          canPickBranch={canPickBranch}
          onBranchChange={handleBranchChange}
          onCreate={categories.openCreate}
        />

        {/* =====================================================
            🔹 Filtros
        ===================================================== */}

        <RawMaterialCategoryFilters
          search={search}
          activeFilter={activeFilter}
          onSearchChange={handleSearchChange}
          onActiveChange={handleActiveChange}
        />

        {/* =====================================================
            🔹 Contenido
        ===================================================== */}

        {categories.loading ? (
          <RawMaterialCategorySkeleton />
        ) : categories.list.length === 0 ? (
          <RawMaterialCategoryEmpty />
        ) : (
          <div className="relative">
            <RawMaterialCategoryTable
              categories={categories.list}
              onEdit={categories.edit}
              onToggleActive={(item) =>
                categories.toggleActive(item, request, previousPage)
              }
            />
            <RawMaterialCategoryGrid
              categories={categories.list}
              onEdit={categories.edit}
              onToggleActive={(item) =>
                categories.toggleActive(item, request, previousPage)
              }
            />
          </div>
        )}

        {/* =====================================================
            🔹 Paginación
        ===================================================== */}

        {categories.page &&
          categories.page.page.totalElements > 0 && (
            <div
              className="
                border-t
                border-stroke

                px-5
                pb-5
                pt-4

                dark:border-strokedark

                sm:px-7
              "
            >
              <BasicPagination
                currentPage={categories.page.page.number}
                totalPages={categories.page.page.totalPages}
                totalElements={
                  categories.page.page.totalElements
                }
                size={categories.page.page.size ?? size}
                first={categories.page.page.number === 0}
                last={categories.page.page.number >= categories.page.page.totalPages - 1}
                onPageChange={setPage}
                onSizeChange={handlePageSizeChange}
              />
            </div>
          )}
      </SectionWrapper>

      {/* =====================================================
          🔹 Modal crear / editar
      ===================================================== */}

      <RawMaterialCategoryFormModal
        isOpen={categories.modalOpen}
        form={categories.form}
        saving={categories.saving}
        onClose={categories.closeModal}
        onChange={categories.updateField}
        onSave={() =>
          categories.save(request, previousPage)
        }
      />
    </WorkspaceLayout>
  );
};

export default RestaurantRawMaterialCategoriesScreen;
